import React, { useState } from 'react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { Box, Button, Container, IconButton, useToast, VStack } from '@chakra-ui/react';
import { createSuperMerchantKYB } from 'api/super-merchant';
import { showError, showToast } from 'utils/notifications';
import Form from 'components/form';
import { FormSectionRenderer } from 'components/form/SectionRenderer';

const SuperMerchantKYB = () => {
  const toast = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    super_merchant_id: '',
    legal_entity_name: '',
    business_name: '',
    entity_type: '',
    website: '',
    address: '',
    email_id: '',
    phone_number: '',
    company_registration_number: '',
    country_of_incorporation: '',
    date_of_incorporation: '',
    partner_id: '',
    applicant_id: '',
    registered_address: '',
    operating_address: '',
    ubo_details: [
      {
        name: '',
        nationality: '',
        dob: '',
        ownership_percentage: '',
      },
    ],
    tnc_consent: {
      signed_timestamp: '',
      viewed_timestamp: '',
      end_user_ip: '',
    },
  });

  const handleUBOChange = (index, field, value) => {
    const updatedUBOs = [...formData.ubo_details];

    updatedUBOs[index] = {
      ...updatedUBOs[index],
      [field]: value,
    };
    setFormData((prev) => ({
      ...prev,
      ubo_details: updatedUBOs,
    }));
  };

  const addUBO = () => {
    setFormData((prev) => ({
      ...prev,
      ubo_details: [
        ...prev.ubo_details,
        {
          name: '',
          nationality: '',
          dob: '',
          ownership_percentage: '',
        },
      ],
    }));
  };

  const removeUBO = (index) => {
    setFormData((prev) => ({
      ...prev,
      ubo_details: prev.ubo_details.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      await createSuperMerchantKYB(formData);
      showToast(toast, 'Success', 'Successfully created super-merchant KYB.', 'success');
    } catch (error) {
      showError(toast, 'Error creating super-merchant KYB', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Section configurations
  const sectionConfigs = [
    {
      title: 'Basic Information',
      fields: [
        { name: 'super_merchant_id', label: 'Super Merchant ID' },
        { name: 'legal_entity_name', label: 'Legal Entity Name' },
        { name: 'business_name', label: 'Business Name' },
        { name: 'entity_type', label: 'Entity Type' },
      ],
    },
    {
      title: 'Contact Information',
      fields: [
        { name: 'website', label: 'Website', type: 'url', isRequired: false },
        { name: 'email_id', label: 'Email', type: 'email' },
        { name: 'phone_number', label: 'Phone Number' },
      ],
    },
    {
      title: 'Address Information',
      fields: [
        { name: 'address', label: 'Address' },
        { name: 'registered_address', label: 'Registered Address' },
        { name: 'operating_address', label: 'Operating Address' },
      ],
    },
    {
      title: 'Company Details',
      fields: [
        { name: 'company_registration_number', label: 'Company Registration Number' },
        { name: 'country_of_incorporation', label: 'Country of Incorporation' },
        { name: 'date_of_incorporation', label: 'Date of Incorporation', type: 'date' },
        { name: 'partner_id', label: 'Partner Id (BUID)', isRequired: false },
        { name: 'applicant_id', label: 'Applicant Id', isRequired: false },
      ],
    },
    {
      title: 'Ultimate Beneficial Owners (UBO)',
      customSection: ({ formData }) => (
        <VStack spacing={4} align='stretch'>
          {formData.ubo_details.map((ubo, index) => (
            <FormSectionRenderer
              key={index}
              title={undefined}
              columns={5}
              fields={[
                {
                  label: 'Name',
                  name: 'name',
                  value: ubo.name,
                  onChange: (e) => handleUBOChange(index, 'name', e.target.value),
                },
                {
                  label: 'Nationality',
                  name: 'nationality',
                  value: ubo.nationality,
                  onChange: (e) => handleUBOChange(index, 'nationality', e.target.value),
                },
                {
                  label: 'Date of Birth',
                  name: 'dob',
                  type: 'date',
                  value: ubo.dob,
                  onChange: (e) => handleUBOChange(index, 'dob', e.target.value),
                },
                {
                  label: 'Ownership %',
                  name: 'ownership_percentage',
                  type: 'number',
                  value: ubo.ownership_percentage,
                  onChange: (e) => handleUBOChange(index, 'ownership_percentage', e.target.value),
                },
                {
                  type: 'custom',
                  render: () => (
                    <IconButton
                      icon={<DeleteIcon />}
                      colorScheme='red'
                      onClick={() => removeUBO(index)}
                      isDisabled={formData.ubo_details.length === 1}
                      aria-label='Delete UBO'
                      mt={8}
                      width='fit-content'
                      key={`delete-ubo-${index}`}
                    />
                  ),
                },
              ]}
              formData={ubo}
            />
          ))}
          <Button leftIcon={<AddIcon />} onClick={addUBO} colorScheme='blue' variant='outline' alignSelf='flex-start'>
            Add UBO
          </Button>
        </VStack>
      ),
    },
    {
      title: 'Terms and Conditions',
      columns: 3,
      fields: [
        {
          name: 'tnc_consent.signed_timestamp',
          label: 'Signed Timestamp',
          type: 'datetime-local',
          isRequired: false,
        },
        {
          name: 'tnc_consent.viewed_timestamp',
          label: 'Viewed Timestamp',
          type: 'datetime-local',
          isRequired: false,
        },
        {
          name: 'tnc_consent.end_user_ip',
          label: 'End User IP',
          isRequired: false,
        },
      ],
    },
  ];

  return (
    <Container maxW='container.xl' py={8}>
      <Box bg='white' borderRadius='lg' boxShadow='lg' p={6}>
        <Form
          sectionConfigs={sectionConfigs}
          formData={formData}
          setFormData={setFormData}
          handleSubmit={handleSubmit}
          isSubmitLoading={isLoading}
          title='Super Merchant KYB Form'
        />
      </Box>
    </Container>
  );
};

export default SuperMerchantKYB;
